import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AppIcon from '../common/AppIcon';
import { Guest } from '../../services/guests';
import { GuestCategory } from '../../services/guestCategories';

interface GuestListProps {
  guests: Guest[];
  categories: GuestCategory[];
  loading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onEdit: (guest: Guest) => void;
  onCategoryChange?: (guest: Guest, field: 'category_level1_id' | 'category_level2_id', categoryId: string | null) => void;
}

type GuestSortField = 'name' | 'phone' | 'email' | 'num_invited' | 'num_expected_to_attend' | 'category_level1_id' | 'category_level2_id';
type SortDir = 'asc' | 'desc';

const GuestList: React.FC<GuestListProps> = ({ guests, categories, loading, selectedIds, onSelectionChange, onEdit, onCategoryChange }) => {
  const theme = useTheme();
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
  const level1Cats = categories.filter((c) => c.level === 1);
  const level2Cats = categories.filter((c) => c.level === 2);

  const [sortField, setSortField] = useState<GuestSortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [catMenuAnchor, setCatMenuAnchor] = useState<HTMLElement | null>(null);
  const [catMenuGuest, setCatMenuGuest] = useState<Guest | null>(null);
  const [catMenuField, setCatMenuField] = useState<'category_level1_id' | 'category_level2_id'>('category_level1_id');

  const handleSort = (field: GuestSortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedGuests = useMemo(() => {
    if (!sortField) return guests;
    return [...guests].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'phone':
          cmp = (a.phone || '').localeCompare(b.phone || '');
          break;
        case 'email':
          cmp = (a.email || '').localeCompare(b.email || '');
          break;
        case 'num_invited':
          cmp = a.num_invited - b.num_invited;
          break;
        case 'num_expected_to_attend':
          cmp = a.num_expected_to_attend - b.num_expected_to_attend;
          break;
        case 'category_level1_id':
          cmp = (catMap[a.category_level1_id || '']?.name || '').localeCompare(catMap[b.category_level1_id || '']?.name || '');
          break;
        case 'category_level2_id':
          cmp = (catMap[a.category_level2_id || '']?.name || '').localeCompare(catMap[b.category_level2_id || '']?.name || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [guests, sortField, sortDir, catMap]);

  const allSelected = guests.length > 0 && selectedIds.size === guests.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < guests.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(guests.map((g) => g.id)));
    }
  };

  const handleToggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const renderCategoryPill = (
    catId: string | null,
    guest: Guest,
    field: 'category_level1_id' | 'category_level2_id',
  ) => {
    const cat = catId ? catMap[catId] : null;
    return (
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setCatMenuAnchor(e.currentTarget as HTMLElement);
          setCatMenuGuest(guest);
          setCatMenuField(field);
        }}
        sx={{ cursor: 'pointer', display: 'inline-block', minWidth: { xs: 0, sm: 40 }, minHeight: 24, maxWidth: '100%', overflow: 'hidden' }}
      >
        {cat ? (
          <Chip
            label={cat.name}
            size="small"
            sx={{
              bgcolor: cat.color ? `${cat.color}25` : 'grey.100',
              color: cat.color || 'grey.800',
              fontWeight: 500,
              border: `1px solid ${cat.color || '#E4DED8'}`,
              cursor: 'pointer',
              maxWidth: '100%',
              '& .MuiChip-label': { px: { xs: 0.75, sm: 1.5 }, overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            —
          </Typography>
        )}
      </Box>
    );
  };

  const catMenuOptions = catMenuField === 'category_level1_id' ? level1Cats : level2Cats;

  if (loading) return null;

  const totalInvited = guests.reduce((sum, g) => sum + g.num_invited, 0);
  const totalExpected = guests.reduce((sum, g) => sum + g.num_expected_to_attend, 0);

  return (
    <>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: { xs: 1.5, sm: 3 },
          bgcolor: 'background.paper',
          maxHeight: { sm: 'calc(100vh - 280px)' },
          overflow: { xs: 'hidden', sm: 'auto' },
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { px: { xs: 0.5, sm: 1.5 } },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell padding="checkbox" sx={{ width: { xs: 36, sm: 64 }, minWidth: { xs: 36, sm: 64 }, pl: { sm: 1 }, pr: { sm: 1 }, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: { xs: 100, md: 140 }, pl: { xs: 1.5, sm: 1.5 }, pr: { xs: 1, sm: 1.5 }, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1 }}>
                <TableSortLabel active={sortField === 'category_level1_id'} direction={sortField === 'category_level1_id' ? sortDir : 'asc'} onClick={() => handleSort('category_level1_id')}>
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 140, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>
                <TableSortLabel active={sortField === 'category_level2_id'} direction={sortField === 'category_level2_id' ? sortDir : 'asc'} onClick={() => handleSort('category_level2_id')}>
                  Sub Category
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, px: { xs: 1, sm: 1.5 }, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, overflow: 'hidden' }}>
                <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 140, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <TableSortLabel active={sortField === 'phone'} direction={sortField === 'phone' ? sortDir : 'asc'} onClick={() => handleSort('phone')}>
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 180, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'none', md: 'table-cell' } }}>
                <TableSortLabel active={sortField === 'email'} direction={sortField === 'email' ? sortDir : 'asc'} onClick={() => handleSort('email')}>
                  Email
                </TableSortLabel>
              </TableCell>
              {/* Desktop: separate Invited and Expected columns */}
              <TableCell sx={{ fontWeight: 600, width: 80, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <TableSortLabel active={sortField === 'num_invited'} direction={sortField === 'num_invited' ? sortDir : 'asc'} onClick={() => handleSort('num_invited')}>
                  Invited
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <TableSortLabel active={sortField === 'num_expected_to_attend'} direction={sortField === 'num_expected_to_attend' ? sortDir : 'asc'} onClick={() => handleSort('num_expected_to_attend')}>
                  Expected
                </TableSortLabel>
              </TableCell>
              {/* Mobile: combined Exp/Inv column */}
              <TableCell sx={{ fontWeight: 600, width: 46, px: { xs: 1, sm: 1.5 }, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, display: { xs: 'table-cell', sm: 'none' }, whiteSpace: 'nowrap', textAlign: 'right', fontSize: '0.7rem' }}>
                Expected/Invited
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: 40, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, p: 0.5, textAlign: 'center', display: { xs: 'none', sm: 'table-cell' } }}>
                <Tooltip title="Dietary restriction">
                  <span style={{ fontSize: 14 }}>🍽️</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: 40, position: 'sticky', top: 0, bgcolor: 'grey.50', zIndex: 1, p: 0.5, display: { xs: 'none', sm: 'table-cell' } }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {guests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No guests yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {sortedGuests.map((guest) => (
                  <TableRow
                    key={guest.id}
                    hover
                    selected={selectedIds.has(guest.id)}
                    onClick={() => onEdit(guest)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ pr: { sm: 1 }, display: { xs: 'none', sm: 'table-cell' } }}>
                      <Checkbox
                        checked={selectedIds.has(guest.id)}
                        onChange={() => handleToggle(guest.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, pl: { xs: 1.5, sm: 1.5 }, pr: { xs: 1, sm: 1.5 }, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
                      {renderCategoryPill(guest.category_level1_id, guest, 'category_level1_id')}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, display: { xs: 'none', md: 'table-cell' } }} onClick={(e) => e.stopPropagation()}>
                      {renderCategoryPill(guest.category_level2_id, guest, 'category_level2_id')}
                    </TableCell>
                    <TableCell
                      dir="auto"
                      sx={{
                        py: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {guest.first_name} {guest.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>{guest.phone}</TableCell>
                    <TableCell sx={{ py: 1.5, display: { xs: 'none', md: 'table-cell' } }}>{guest.email}</TableCell>
                    {/* Desktop: separate invited/expected cells */}
                    <TableCell sx={{ py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>{guest.num_invited}</TableCell>
                    <TableCell sx={{ py: 1.5, display: { xs: 'none', sm: 'table-cell' } }}>{guest.num_expected_to_attend}</TableCell>
                    {/* Mobile: combined exp/inv cell (expected first) */}
                    <TableCell sx={{ py: 1.5, display: { xs: 'table-cell', sm: 'none' }, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {guest.num_expected_to_attend}/{guest.num_invited}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, p: 0.5, textAlign: 'center', display: { xs: 'none', sm: 'table-cell' } }}>
                      {guest.dietary_restriction && (
                        <Tooltip title={
                          guest.dietary_restriction === 'other'
                            ? `Other: ${guest.dietary_restriction_note || ''}`
                            : guest.dietary_restriction.charAt(0).toUpperCase() + guest.dietary_restriction.slice(1)
                        }>
                          <span style={{ fontSize: 14 }}>
                            {guest.dietary_restriction === 'vegan' ? '🌱' : guest.dietary_restriction === 'vegetarian' ? '🥛' : '⚠️'}
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, p: 0.5, textAlign: 'center', display: { xs: 'none', sm: 'table-cell' } }}>
                      {guest.notes && (
                        <Tooltip title={guest.notes}>
                          <Box sx={{ display: 'inline-flex', color: 'text.secondary' }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.50', position: 'sticky', bottom: 0, zIndex: 1, display: { xs: 'none', sm: 'table-row' } }}>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none', bgcolor: 'grey.50' }} />
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none', bgcolor: 'grey.50' }} />
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none', bgcolor: 'grey.50', display: { xs: 'none', md: 'table-cell' } }} />
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none', bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Total ({guests.length} guest{guests.length !== 1 ? 's' : ''})
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none', bgcolor: 'grey.50', display: { xs: 'none', sm: 'table-cell' } }} />
                  <TableCell sx={{ borderBottom: 'none', bgcolor: 'grey.50', display: { xs: 'none', md: 'table-cell' } }} />
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none', bgcolor: 'grey.50' }}>{totalInvited}</TableCell>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none', bgcolor: 'grey.50' }}>{totalExpected}</TableCell>
                  <TableCell sx={{ borderBottom: 'none', bgcolor: 'grey.50', display: { xs: 'none', sm: 'table-cell' } }} />
                  <TableCell sx={{ borderBottom: 'none', bgcolor: 'grey.50', display: { xs: 'none', sm: 'table-cell' } }} />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Category picker menu */}
      <Menu
        anchorEl={catMenuAnchor}
        open={!!catMenuAnchor}
        onClose={() => { setCatMenuAnchor(null); setCatMenuGuest(null); }}
      >
        <MenuItem
          onClick={() => {
            if (catMenuGuest && onCategoryChange) onCategoryChange(catMenuGuest, catMenuField, null);
            setCatMenuAnchor(null);
            setCatMenuGuest(null);
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>None</Typography>
        </MenuItem>
        {catMenuOptions.map((cat) => (
          <MenuItem
            key={cat.id}
            selected={catMenuGuest?.[catMenuField] === cat.id}
            onClick={() => {
              if (catMenuGuest && onCategoryChange) onCategoryChange(catMenuGuest, catMenuField, cat.id);
              setCatMenuAnchor(null);
              setCatMenuGuest(null);
            }}
          >
            <Chip
              label={cat.name}
              size="small"
              sx={{
                bgcolor: cat.color ? `${cat.color}25` : 'grey.100',
                color: cat.color || 'grey.800',
                fontWeight: 500,
                border: `1px solid ${cat.color || '#E4DED8'}`,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default GuestList;
